'use client'
import * as React from 'react'
import { useTranslations } from 'next-intl'
import { tw } from 'utils/classnames'
import dynamic from 'next/dynamic'
import { useUserAuthModal } from '@repo/user-auth-dialog/user-auth-dialog-context'
import { useIsLoggedInQuery } from '@repo/analytics/hooks/use-is-logged-in-query'
import { isIosDevice, isSafari as isSafariBrowser } from 'utils/device'

import { useContentMyReviewQuery } from '@web/gen/content'
import { CoinIcon } from '@web/components/icons/coin'
import { useShowCoins } from '../rating/use-show-coins'
import { RatingInput } from '../rating/rating/input'
import { ReviewButton } from '../rating/review-button'
import { useWasDownloadedState } from '@web/app/[locale]/(web)/prod/[slug]/product-asset-context'
import { useProductId } from '@web/app/[locale]/(web)/prod/[slug]/product-id-context'
import { useIsFirstDownload } from '@web/hooks/use-product-data'

const RatingModal = dynamic(() => import('../rating/modal').then(mod => mod.RatingModal), {
  ssr: false,
})

export const COINS_REWARD = 1000

export function HelpCrafter() {
  const [wasDownloaded] = useWasDownloadedState()
  const productId = useProductId()
  const t = useTranslations('prod.product_description_page.buy.help_crafter')
  const isAuthenticatedQuery = useIsLoggedInQuery()
  const isAuthenticated = isAuthenticatedQuery.data
  const { openModal } = useUserAuthModal()
  const [rating, setRating] = React.useState(0)
  const [isModalOpen, setIsModalOpen] = React.useState(false)
  const isIos = isIosDevice()
  const isSafari = isSafariBrowser()
  const { refetch: refetchLicenses, isLoading: isLoadingIsFirstDownload } = useIsFirstDownload(
    isIos && isSafari && Boolean(productId),
  )
  const showCoins = useShowCoins()
  const title = showCoins
    ? t.rich('title_coins', {
        coin: () => <CoinIcon className="inline-block text-[#F9A050]" />,
        number: COINS_REWARD.toLocaleString('en-US'),
      })
    : t('title')
  const { data: myRating } = useContentMyReviewQuery(
    {
      productId: productId ?? '',
    },
    {
      enabled: isAuthenticated,
      select(data) {
        return data?.contentMyReview?.rating ?? 0
      },
    },
  )

  const onChangeAndOpen = async (value: number) => {
    if (!isLoadingIsFirstDownload) {
      await refetchLicenses()
    }
    if (isAuthenticated) {
      setRating(value)
      setIsModalOpen(true)
    } else {
      openModal()
    }
  }

  // This component conditionally renders for users that didn't yet submit a review; we want to keep the modal open even after they submit otherwise the confirmation screens will just flicker
  if ((!wasDownloaded || myRating) && !isModalOpen) {
    return null
  }

  return (
    <div
      className={tw(
        'animate-fade-in shadow-cf-elevation-light-1 rounded-4 3xl:flex-row flex flex-row flex-wrap items-center justify-start p-6 min-[575px]:flex-col min-[575px]:items-start',
      )}
    >
      <div className="text-16 font-bold">{title}</div>
      <div className="text-14 mb-2">{t('description')}</div>

      <div className="flex w-full flex-row flex-wrap items-center justify-start gap-2">
        <RatingInput
          id="downloadRating"
          value={rating}
          onChange={onChangeAndOpen}
          className="h-8"
        />
        <ReviewButton
          setIsModalOpen={async () => {
            if (!isLoadingIsFirstDownload) {
              await refetchLicenses()
            }
            isAuthenticated ? setIsModalOpen(true) : openModal()
          }}
        >
          {t('review_cta')}
        </ReviewButton>
      </div>
      <RatingModal
        rating={rating}
        setRating={setRating}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setRating(myRating ?? 0)
        }}
      />
    </div>
  )
}
